'use client'

import {
    Catalog,
    CatalogCategory,
    addCatalogPartner,
    createCatalog,
    getCatalog,
    getCatalogsByCompany,
} from '@/lib/api/catalog/catalog-api'
import {
    applyPriceFieldChange,
    parseCurrencyToCents,
    PriceDraft,
} from '@/lib/api/catalog/catalog-price-format'
import { TussSelector } from '@/components/tuss/TussSelector'
import {
    useCatalogCategoryLabels,
    useServiceLabel,
} from '@/lib/hooks/use-service-label'
import { useCallback, useEffect, useState } from 'react'
import { MdAdd, MdClose, MdLink, MdSearch } from 'react-icons/md'
import { toast } from 'react-toastify'

interface Props {
    open: boolean
    onClose: () => void
    /** Texto digitado na busca que não retornou nada — vira o nome sugerido. */
    initialName: string
    category: CatalogCategory
    companyId: number
    /**
     * Parceiro selecionado. É o dono do preço (o item nasce sem preço de filial)
     * e o alvo do vínculo — é o que faz o item aparecer na busca funilada.
     */
    partnerId?: number | null
    partnerName?: string
    /**
     * Procedimento exige código TUSS; consulta/exame não. Só vale na saúde — o
     * componente cruza com o segmento antes de exigir (ver `tussRequired`).
     */
    requireTuss?: boolean
    /**
     * Esconde os campos de preço e cria o item sem valor nenhum.
     *
     * É o caso do PEDIDO médico: o exame que o paciente leva ao laboratório não é
     * vendido por quem pede, e obrigar o médico a arbitrar um preço para cadastrar o
     * item no meio da consulta é pedir um número que ele não tem. O TUSS entra como
     * opcional, porque é ele que a guia usa depois.
     */
    hidePricing?: boolean
    /**
     * Não conferir se o item já existe, nem sugerir os parecidos.
     *
     * A conferência existe porque a busca da tela de agendamento é funilada por
     * parceiro, e ali "não achei" NÃO significa "não existe". Onde a busca já varre o
     * catálogo inteiro — o pedido de exames do prontuário —, ela repete a mesma lista
     * que o operador acabou de ver e só atrasa quem já concluiu que o item falta.
     */
    skipExistingCheck?: boolean
    /** Item pronto para uso: recém-criado ou recém-atrelado ao parceiro. */
    onReady: (catalog: Catalog) => void
}

interface Draft extends PriceDraft {
    name: string
    tussCode: string
    collectionMaterial: string
}

/**
 * Cadastro rápido de item do catálogo a partir de uma busca sem resultado.
 *
 * O ponto não óbvio: quando há parceiro selecionado, a busca da tela é funilada
 * por ele, então "não achei" NÃO significa "não existe" — o item pode existir no
 * catálogo e só não estar atrelado àquele parceiro. Criar um novo ali geraria
 * duplicata.
 *
 * Por isso este componente sempre refaz a busca **sem o filtro de parceiro**
 * antes de oferecer a criação: achando, o caminho é atrelar o item existente ao
 * parceiro (com o preço dele); não achando, cria do zero.
 */
export function CatalogQuickCreate({
    open,
    onClose,
    initialName,
    category,
    companyId,
    partnerId,
    partnerName,
    requireTuss = false,
    hidePricing = false,
    skipExistingCheck = false,
    onReady,
}: Props) {
    // TUSS é a Tabela 22 da ANS: fora da saúde não há código para informar, e o
    // campo obrigatório TRAVAVA o cadastro rápido — o operador de um salão não
    // conseguia criar o serviço pela busca sem resultado. O gate mora aqui, e não
    // nos dois call sites, para o próximo consumidor não reabrir o buraco.
    const { isHealth } = useServiceLabel()
    const categoryLabels = useCatalogCategoryLabels()
    const tussRequired = requireTuss && isHealth
    // Sem preço na tela, o TUSS é a única coisa que ainda vale perguntar — e é o que
    // a guia do exame usa depois.
    const showTuss = tussRequired || (hidePricing && isHealth)
    const [checking, setChecking] = useState(false)
    const [existing, setExisting] = useState<Catalog[]>([])
    const [submitting, setSubmitting] = useState(false)
    const [draft, setDraft] = useState<Draft>({
        name: '',
        tussCode: '',
        collectionMaterial: '',
        price: '',
        sellingPrice: '',
        markupPercentage: '',
    })

    // Busca global (sem parceiro): é ela que distingue "não existe" de
    // "existe mas não é deste parceiro".
    const searchWholeCatalog = useCallback(async () => {
        if (!companyId || skipExistingCheck) return
        setChecking(true)
        try {
            const res = await getCatalogsByCompany(companyId, {
                search: initialName.trim() || undefined,
                category,
                activeOnly: true,
                page: 1,
                limit: 10,
            })
            setExisting(res.success && Array.isArray(res.result) ? res.result : [])
        } catch {
            setExisting([])
        } finally {
            setChecking(false)
        }
    }, [companyId, initialName, category, skipExistingCheck])

    useEffect(() => {
        if (!open) return
        setDraft({
            name: initialName.trim(),
            tussCode: '',
            collectionMaterial: '',
            price: '',
            sellingPrice: '',
            markupPercentage: '',
        })
        setExisting([])
        searchWholeCatalog()
    }, [open, initialName, searchWholeCatalog])

    if (!open) return null

    const onPriceField = (
        field: 'price' | 'sellingPrice' | 'markupPercentage',
        value: string
    ) => {
        setDraft(prev => ({
            ...applyPriceFieldChange(prev, field, value),
            name: prev.name,
            tussCode: prev.tussCode,
        }))
    }

    /** Preços do formulário no formato do vínculo de parceiro. */
    const pricingPayload = () => ({
        price: parseCurrencyToCents(draft.price) || undefined,
        sellingPrice: parseCurrencyToCents(draft.sellingPrice) || undefined,
        markupPercentage: draft.markupPercentage
            ? parseFloat(draft.markupPercentage)
            : undefined,
    })

    /**
     * Item já existe no catálogo: atrela ao parceiro (com o preço digitado) e
     * devolve. Sem parceiro, é só selecionar — não há o que atrelar.
     */
    const handleAttach = async (item: Catalog) => {
        if (!partnerId) {
            onReady(item)
            onClose()
            return
        }

        setSubmitting(true)
        try {
            const res = await addCatalogPartner(item.id, {
                partnerId,
                ...pricingPayload(),
            })
            if (!res.success) {
                toast.error(res.error || 'Falha ao atrelar item ao parceiro')
                return
            }
            // Recarrega para trazer o vínculo recém-criado: a tela de agendamento
            // lê o preço a partir de `partners` no item.
            const fresh = await getCatalog(item.id)
            toast.success('Item atrelado ao parceiro')
            onReady(fresh.success && fresh.result ? fresh.result : item)
            onClose()
        } catch {
            toast.error('Falha ao atrelar item ao parceiro')
        } finally {
            setSubmitting(false)
        }
    }

    const handleCreate = async () => {
        const name = draft.name.trim()
        if (!name) {
            toast.error('Informe o nome do item')
            return
        }
        if (tussRequired && !draft.tussCode.trim()) {
            toast.error('Informe o código TUSS do procedimento')
            return
        }

        const pricing = pricingPayload()

        setSubmitting(true)
        try {
            // Com parceiro, o preço é DELE: o item nasce sem preço de filial
            // (0) e os valores vão para o vínculo. Sem parceiro, o preço é da
            // filial mesmo.
            const res = await createCatalog({
                name,
                category,
                companyId,
                tussCode: draft.tussCode.trim() || undefined,
                collectionMaterial:
                    draft.collectionMaterial.trim() || undefined,
                active: true,
                price: partnerId ? 0 : (pricing.price ?? 0),
                sellingPrice: partnerId ? undefined : pricing.sellingPrice,
                markupPercentage: partnerId
                    ? undefined
                    : pricing.markupPercentage,
            })
            if (!res.success || !res.result) {
                toast.error(res.error || 'Falha ao criar item no catálogo')
                return
            }

            const created = res.result
            if (partnerId) {
                const link = await addCatalogPartner(created.id, {
                    partnerId,
                    ...pricing,
                })
                if (!link.success) {
                    // O item foi criado; só o vínculo falhou. Avisa em vez de
                    // seguir calado — sem o vínculo ele some da busca funilada.
                    toast.error(
                        link.error ||
                            'Item criado, mas falhou ao atrelar ao parceiro. Atrele pela tela do parceiro.'
                    )
                }
            }

            const fresh = await getCatalog(created.id)
            toast.success('Item criado no catálogo')
            onReady(fresh.success && fresh.result ? fresh.result : created)
            onClose()
        } catch {
            toast.error('Falha ao criar item no catálogo')
        } finally {
            setSubmitting(false)
        }
    }

    const categoryLabel = categoryLabels[category] || category

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h3 className="font-bold text-lg">
                            Adicionar {categoryLabel.toLowerCase()} ao catálogo
                        </h3>
                        {!!partnerId && (
                            <p className="text-sm text-base-content/60 mt-1">
                                O preço informado será o do parceiro
                                {partnerName ? (
                                    <>
                                        {' '}
                                        <strong>{partnerName}</strong>
                                    </>
                                ) : (
                                    ' selecionado'
                                )}
                                , não o da filial.
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-sm btn-ghost btn-circle"
                        aria-label="Fechar"
                    >
                        <MdClose className="text-lg" />
                    </button>
                </div>

                {skipExistingCheck ? null : checking ? (
                    <div className="flex items-center gap-2 p-4 text-sm text-base-content/60">
                        <span className="loading loading-spinner loading-sm" />
                        Conferindo se já existe no catálogo...
                    </div>
                ) : (
                    existing.length > 0 && (
                        <div className="rounded-xl border border-warning/40 bg-warning/10 p-4 mb-4">
                            <p className="flex items-center gap-2 font-medium text-sm">
                                <MdSearch className="text-lg" />
                                {partnerId
                                    ? 'Já existe no catálogo, mas não está atrelado a este parceiro'
                                    : 'Já existe no catálogo'}
                            </p>
                            <p className="text-xs text-base-content/70 mt-1 mb-3">
                                {partnerId
                                    ? 'Atrele um destes ao parceiro com o preço abaixo, em vez de criar um item duplicado.'
                                    : 'Selecione um destes em vez de criar um item duplicado.'}
                            </p>
                            <div className="space-y-2">
                                {existing.map(item => (
                                    <div
                                        key={item.id}
                                        className="flex items-center gap-2 bg-base-100 rounded-lg p-2"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">
                                                {item.name}
                                            </p>
                                            {item.tussCode && (
                                                <p className="text-xs text-base-content/60">
                                                    TUSS {item.tussCode}
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleAttach(item)}
                                            disabled={submitting}
                                            className="btn btn-sm btn-secondary gap-1 flex-shrink-0"
                                        >
                                            <MdLink className="text-base" />
                                            {partnerId ? 'Atrelar' : 'Usar'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )
                )}

                <div className="space-y-4">
                    <div className="divider text-xs text-base-content/50 my-2">
                        {existing.length > 0
                            ? 'ou crie um item novo'
                            : 'Novo item'}
                    </div>

                    <div className="form-control">
                        <label className="label py-1">
                            <span className="label-text text-sm font-medium">
                                Nome *
                            </span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            value={draft.name}
                            onChange={e =>
                                setDraft(prev => ({
                                    ...prev,
                                    name: e.target.value,
                                }))
                            }
                            placeholder="Nome do item"
                        />
                    </div>

                    {/* Material de coleta: agrupa o pedido impresso por tubo. Só no
                        EXAME e na saúde — quem cadastra uma escova progressiva não
                        coleta nada. Cadastrar aqui e não preencher o material joga o
                        exame para o fim da folha, sem cabeçalho. */}
                    {category === 'EXAM' && isHealth && (
                        <div className="form-control w-full">
                            <label className="label pb-1">
                                <span className="label-text font-medium">
                                    Material de coleta
                                </span>
                            </label>
                            <input
                                type="text"
                                list="quick-create-collection-materials"
                                className="input input-bordered w-full"
                                placeholder="Ex.: Sangue"
                                value={draft.collectionMaterial}
                                onChange={e =>
                                    setDraft(prev => ({
                                        ...prev,
                                        collectionMaterial: e.target.value,
                                    }))
                                }
                            />
                            <datalist id="quick-create-collection-materials">
                                {[
                                    'Sangue',
                                    'Urina',
                                    'Fezes',
                                    'Escarro',
                                    'Secreção',
                                    'Swab',
                                    'Saliva',
                                    'Líquor',
                                ].map(material => (
                                    <option key={material} value={material} />
                                ))}
                            </datalist>
                        </div>
                    )}

                    {showTuss && (
                        <TussSelector
                            value={draft.tussCode || null}
                            label={
                                tussRequired
                                    ? 'Código TUSS *'
                                    : 'Código TUSS (opcional)'
                            }
                            placeholder="Buscar por código ou termo TUSS..."
                            onChange={(code, tuss) =>
                                setDraft(prev => ({
                                    ...prev,
                                    tussCode: code || '',
                                    // Nome ainda vazio herda o termo do TUSS; se o
                                    // operador já digitou algo, respeita o que ele
                                    // escreveu.
                                    name: prev.name || tuss?.term || '',
                                }))
                            }
                        />
                    )}

                    <div
                        className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${
                            hidePricing ? 'hidden' : ''
                        }`}
                    >
                        <div className="form-control">
                            <label className="label py-1">
                                <span className="label-text text-sm font-medium">
                                    Preço de custo
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={draft.price}
                                onChange={e =>
                                    onPriceField('price', e.target.value)
                                }
                                placeholder="R$ 0,00"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label py-1">
                                <span className="label-text text-sm font-medium">
                                    Preço de venda
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={draft.sellingPrice}
                                onChange={e =>
                                    onPriceField('sellingPrice', e.target.value)
                                }
                                placeholder="R$ 0,00"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label py-1">
                                <span className="label-text text-sm font-medium">
                                    Markup (%)
                                </span>
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                className="input input-bordered w-full"
                                value={draft.markupPercentage}
                                onChange={e =>
                                    onPriceField(
                                        'markupPercentage',
                                        e.target.value
                                    )
                                }
                                placeholder="0"
                            />
                        </div>
                    </div>
                </div>

                <div className="modal-action">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-ghost"
                        disabled={submitting}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={submitting || checking}
                        className="btn btn-secondary gap-1"
                    >
                        {submitting ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdAdd className="text-lg" />
                        )}
                        Criar e usar
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}

export default CatalogQuickCreate
